// Tier 2 / F-21 — emoji shortcode lookup.
//
// Reuses the bundled English emoji metadata from emoji-picker-react
// (already a project dependency, used by EmojiPickerButton.jsx) so
// we don't ship a second emoji table.
//
// The data shape per emoji is: { n: [keyword, keyword, full name], u: 'unified-hex', a: '...' }
// We flatten across all categories and convert each unified hex
// codepoint string (e.g. '1f604' or '1f1ec-1f1e7') into the actual
// emoji character via String.fromCodePoint.

import enData from 'emoji-picker-react/dist/data/emojis-en.json';

// Convert a unified hex string ('1f600' or '1f1ec-1f1e7') to the
// rendered emoji glyph.
const unifiedToEmoji = (unified) => {
    if (!unified) return '';
    try {
        const codePoints = unified
            .split('-')
            .map((part) => parseInt(part, 16))
            .filter((n) => Number.isFinite(n));
        if (codePoints.length === 0) return '';
        return String.fromCodePoint(...codePoints);
    } catch (_) {
        return '';
    }
};

// Build a normalised lookup list once per module load. Each entry
// has { name, keywords, emoji } so the autocomplete can do simple
// substring filtering.
const buildIndex = () => {
    const out = [];
    const seen = new Set();
    const cats = enData?.emojis || {};
    Object.keys(cats).forEach((cat) => {
        const arr = cats[cat] || [];
        arr.forEach((row) => {
            const u = row?.u;
            const names = Array.isArray(row?.n) ? row.n : [];
            const emoji = unifiedToEmoji(u);
            if (!emoji || seen.has(u)) return;
            seen.add(u);
            // Convention: the LAST entry in `n` is the human-readable
            // full name, prior entries are keyword aliases.
            const fullName = names[names.length - 1] || names[0] || u;
            out.push({
                emoji,
                name: fullName,
                // Pre-lowercase for case-insensitive prefix matching.
                searchable: names.map((n) => String(n).toLowerCase()),
            });
        });
    });
    return out;
};

let CACHED_INDEX = null;
const getIndex = () => {
    if (CACHED_INDEX) return CACHED_INDEX;
    CACHED_INDEX = buildIndex();
    return CACHED_INDEX;
};

// Public — search by keyword query. Returns up to `limit` emojis,
// ranked: prefix matches on the full name first, then any
// substring match on any keyword.
export const searchEmojiShortcodes = (rawQuery, limit = 8) => {
    const q = String(rawQuery || '').trim().toLowerCase();
    if (q.length < 2) return [];
    const index = getIndex();
    const prefixHits = [];
    const otherHits = [];
    // Iterate the full index so substring matches aren't dropped when prefix
    // hits fill the limit. The index is ~1500 entries — sub-millisecond on
    // any modern engine. Early-break on the prefix bucket alone hid useful
    // alternatives (e.g. typing `:smi` returned 8 prefix hits and the user
    // never saw "smile" entries that only matched as substrings).
    for (let i = 0; i < index.length; i += 1) {
        const row = index[i];
        if (row.searchable[row.searchable.length - 1].startsWith(q)) {
            prefixHits.push(row);
        } else if (row.searchable.some((n) => n.includes(q))) {
            otherHits.push(row);
        }
    }
    const merged = prefixHits.concat(otherHits).slice(0, limit);
    return merged;
};

export default searchEmojiShortcodes;
